const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: MuiPaper } = require('@mui/material/Paper');
const { default: MuiDialog } = require('@mui/material/Dialog');
const { default: MuiAppBar } = require('@mui/material/AppBar');
const { default: MuiTypography } = require('@mui/material/Typography');
const { default: MuiToolbar } = require('@mui/material/Toolbar');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: CloseIcon } = require('@mui/icons-material/Close');

const {
    APP_SHELL_CONTENT_MAX_WIDTH,
    APP_SHELL_CONTENT_PADDING_X,
    APP_SHELL_CONTENT_PADDING_Y,
    APP_SHELL_CONTENT_PADDING_BOTTOM
} = require('components/AppShell');

const {
    ELEMENT_IDS,
    MAIN_HEADER_HEIGHT
} = require('utils/constants');

const Snackbar = require('containers/Snackbar');

const FocusIndicator = require('components/FocusIndicator');

const {
    useRef,
    useEffect
} = React;

const internals = {};

module.exports = function FullScreenDialog(props) {

    const {
        children,
        onRequestClose,
        title,
        open,
        fixedToTop = null,
        fullWidth,
        ...rest
    } = props;

    const {
        Dialog,
        Paper,
        WithTopbarPaper,
        Header,
        FixedToTopWrapper,
        InnerWrapper,
        ScrollContainer,
        OuterBreakoutContainer,
        InnerBreakoutMaxWidthContainer,
        StyledAppBar,
        StyledToolbar
    } = internals;

    const titleRef = useRef();
    const paperRef = useRef();

    useEffect(() => {

        if (!open) {
            // TODO This is a bit wonky, could be better,
            // the ref situation is difficult.
            // This is for a11y, to announce the dialog title to screen readers.
            titleRef.current = 'focus-on-next-render';
            FocusIndicator.onBlurHandler();
        }
    }, [open]);

    const onTitleRef = (ref) => {

        // This is for a11y, to announce the dialog title to screen readers.
        if (open && ref && titleRef.current === 'focus-on-next-render') {
            titleRef.current = ref;
            ref.focus();
            FocusIndicator.wrapFocusIndicatorForElement();
        }
    };

    const onScroll = (type) => {

        FocusIndicator.wrapFocusIndicatorForElement();

        if (type === 'paper' && paperRef.current) {
            const focusIndicator = document.getElementById(ELEMENT_IDS.focusIndicator);
            const paperBoundingRect = paperRef.current.getBoundingClientRect();
            const focusIndicatorBoundingRect = focusIndicator.getBoundingClientRect();

            if (focusIndicatorBoundingRect.top < paperBoundingRect.top) {
                FocusIndicator.onBlurHandler();
            }
            else if (focusIndicatorBoundingRect.top >= paperBoundingRect.top + paperBoundingRect.height - focusIndicatorBoundingRect.height) {
                FocusIndicator.onBlurHandler();
            }
            else {
                FocusIndicator.wrapFocusIndicatorForElement();
            }
        }
        else {
            FocusIndicator.wrapFocusIndicatorForElement();
        }
    };

    return (
        <Dialog
            fullWidth
            fullScreen
            open={open}
            onClose={onRequestClose}
            $hasFixedToTop={!!fixedToTop}
            // Temp fix since it seems material-ui internals are setting
            // this property to something dynamic, and it's causing a WAVE error
            aria-labelledby=''
            {...rest}
        >
            <StyledAppBar
                position='fixed'
                style={{ borderRadius: 0 }}
            >
                <StyledToolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onRequestClose}
                        aria-label="close"
                        size="large"
                        data-focus-outline='radius:40,padding:-4'
                    >
                        <CloseIcon />
                    </IconButton>
                    <Header
                        tabIndex={0}
                        data-focus-outline='radius:8,padding:4'
                        ref={onTitleRef}
                    >
                        {title}
                    </Header>
                </StyledToolbar>
            </StyledAppBar>
            <InnerWrapper>
                {fixedToTop && (
                    <FixedToTopWrapper>
                        {fixedToTop}
                    </FixedToTopWrapper>
                )}
                <ScrollContainer
                    onScroll={() => onScroll('container')}
                >
                    {fixedToTop ?
                        <WithTopbarPaper elevation={0} onScroll={() => onScroll('paper')} ref={paperRef}>
                            {!fullWidth && children}
                            {fullWidth && (
                                <OuterBreakoutContainer>
                                    <InnerBreakoutMaxWidthContainer>
                                        {children}
                                    </InnerBreakoutMaxWidthContainer>
                                </OuterBreakoutContainer>
                            )}
                        </WithTopbarPaper> :
                        <Paper elevation={0} onScroll={() => onScroll('paper')} ref={paperRef}>
                            {!fullWidth && children}
                            {fullWidth && (
                                <OuterBreakoutContainer>
                                    <InnerBreakoutMaxWidthContainer>
                                        {children}
                                    </InnerBreakoutMaxWidthContainer>
                                </OuterBreakoutContainer>
                            )}
                        </Paper>
                    }
                </ScrollContainer>
            </InnerWrapper>
            <Snackbar />
        </Dialog>
    );
};

module.exports.propTypes = {
    children: T.node.isRequired,
    open: T.bool.isRequired,
    onRequestClose: T.func.isRequired,
    title: T.oneOfType([T.string, T.element]),
    fixedToTop: T.element,
    fullWidth: T.bool
};

internals.InnerWrapper = Styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    flex-flow: column nowrap;
    background: linear-gradient(
        to bottom,
        black 0,
        black var(--safezonetop),
        ${({ theme }) => theme.palette.emptyBackground} var(--safezonetop),
        ${({ theme }) => theme.palette.emptyBackground} calc(100% - var(--safezonebottom)),
        black calc(100% - var(--safezonebottom)),
        black 100%
    );
    padding-top: calc(var(--safezonetop) + ${MAIN_HEADER_HEIGHT}px);
    padding-bottom: var(--safezonebottom);
`;

internals.ScrollContainer = Styled.div`
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.palette.emptyBackground};

    flex-grow: 1;
    display: flex;
    flex-flow: column nowrap;
    overflow-y: scroll;
    overflow-x: hidden;

    .MuiPaper-root {
        flex-grow: 1;
    }
`;

internals.Paper = Styled(MuiPaper)`
    max-width: ${APP_SHELL_CONTENT_MAX_WIDTH};
    margin: auto;
    width: 100%;
    padding: ${APP_SHELL_CONTENT_PADDING_Y} ${APP_SHELL_CONTENT_PADDING_X} ${APP_SHELL_CONTENT_PADDING_BOTTOM};
    border-radius: 0 !important;
    box-shadow: none !important;
`;

internals.WithTopbarPaper = Styled(MuiPaper)`
    margin: auto;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding: ${APP_SHELL_CONTENT_PADDING_Y} ${APP_SHELL_CONTENT_PADDING_X} ${APP_SHELL_CONTENT_PADDING_BOTTOM};
    border-radius: 0 !important;
    box-shadow: none !important;
`;

internals.FixedToTopWrapper = Styled.div`
    z-index: 1;
    width: 100%;
`;

internals.Header = Styled(MuiTypography).attrs({
    variant: 'h1',
    align: 'center',
    color: 'inherit'
})`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: -1;
    font-weight: 400;
    font-size: 30px;
    line-height: 30px;
`;

internals.Dialog = Styled(MuiDialog)`
    .MuiDialog-paper {
        /* The shared Paper outline must not frame the black safe areas. */
        border: 0;
        background-color: #000;
        overflow-x: hidden;
        overflow-y: ${({ $hasFixedToTop }) => {

        return $hasFixedToTop ? 'hidden' : 'auto';
    }};
    }
`;

internals.OuterBreakoutContainer = Styled.div`
    flex-grow: 1;
    overflow: auto;
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
`;

internals.InnerBreakoutMaxWidthContainer = Styled.div`
    max-width: ${APP_SHELL_CONTENT_MAX_WIDTH};
    margin: auto;
    padding: 0 ${APP_SHELL_CONTENT_PADDING_X};
`;

internals.StyledAppBar = Styled(MuiAppBar)`
    width: 100%;
    height: ${MAIN_HEADER_HEIGHT}px;
    position: fixed;
    top: var(--safezonetop);
`;

internals.StyledToolbar = Styled(MuiToolbar)`
    height: ${MAIN_HEADER_HEIGHT}px;
`;
